/**
 * FileTree Component
 * 
 * Displays a hierarchical file tree for cloud code navigation.
 * Features:
 * - Expand/collapse folders
 * - File type icons
 * - Selected file highlighting
 * - Smooth animations
 */

import { useState, useCallback, useMemo } from 'react';
import { CloudTreeNode } from '../../../../types/AppPanel';

interface FileTreeProps {
  /** Root nodes of the tree */
  tree: CloudTreeNode[];
  /** Currently selected file path */
  selectedPath?: string;
  /** Callback when a file is selected */
  onFileSelect: (node: CloudTreeNode, path: string) => void;
  /** Filter to show only specific folders (e.g., ['cloud']) */
  rootFilter?: string[];
}

export default function FileTree({
  tree,
  selectedPath,
  onFileSelect,
  rootFilter,
}: FileTreeProps) {
  // Track which folders are expanded
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(
    new Set(['cloud', 'cloud/chatgpt'])
  );

  // Toggle folder expansion
  const toggleFolder = useCallback((path: string) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  // Filter root nodes if specified
  const filteredTree = useMemo(() => {
    if (!rootFilter || rootFilter.length === 0) return tree;
    return tree.filter(node => rootFilter.includes(node.text));
  }, [tree, rootFilter]);

  if (filteredTree.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-white/40 text-sm p-4">
        No files found
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto glass-scroll py-2">
      {filteredTree.map(node => (
        <TreeNode
          key={node.text}
          node={node}
          path={node.text}
          depth={0}
          expandedPaths={expandedPaths}
          selectedPath={selectedPath}
          onToggle={toggleFolder}
          onSelect={onFileSelect}
        />
      ))}
    </div>
  );
}

// ============================================================================
// TreeNode - Recursive component for each node in the tree
// ============================================================================

interface TreeNodeProps {
  node: CloudTreeNode;
  path: string;
  depth: number;
  expandedPaths: Set<string>;
  selectedPath?: string;
  onToggle: (path: string) => void;
  onSelect: (node: CloudTreeNode, path: string) => void;
}

function TreeNode({
  node,
  path,
  depth,
  expandedPaths,
  selectedPath,
  onToggle,
  onSelect,
}: TreeNodeProps) {
  const isFolder = node.type === 'folder';
  const isExpanded = expandedPaths.has(path);
  const isSelected = selectedPath === path;

  const handleClick = () => {
    if (isFolder) {
      onToggle(path);
    } else {
      onSelect(node, path);
    }
  };

  return (
    <div>
      {/* Node row */}
      <button
        type="button"
        onClick={handleClick}
        className={`
          w-full flex items-center gap-1.5 px-2 py-1 text-left text-sm
          hover:bg-white/5 transition-colors duration-150
          ${isSelected ? 'bg-[#208AEC]/20 text-[#208AEC]' : 'text-white/70'}
        `}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
      >
        {/* Expand/collapse chevron for folders */}
        {isFolder ? (
          <span className={`
            flex-shrink-0 w-4 h-4 flex items-center justify-center
            transition-transform duration-200
            ${isExpanded ? 'rotate-90' : ''}
          `}>
            <ChevronIcon />
          </span>
        ) : (
          <span className="flex-shrink-0 w-4" />
        )}

        {/* File/folder icon */}
        <span className="flex-shrink-0">
          {isFolder ? (
            <FolderIcon isOpen={isExpanded} />
          ) : (
            <FileIcon filename={node.text} />
          )}
        </span>

        {/* Name */}
        <span className="truncate flex-1">{node.text}</span>
      </button>

      {/* Children (if folder is expanded) */}
      {isFolder && isExpanded && node.children && (
        <div className="animate-in slide-in-from-top-1 duration-200">
          {node.children.map(child => (
            <TreeNode
              key={child.text}
              node={child}
              path={`${path}/${child.text}`}
              depth={depth + 1}
              expandedPaths={expandedPaths}
              selectedPath={selectedPath}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Icons
// ============================================================================

function ChevronIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function FolderIcon({ isOpen }: { isOpen: boolean }) {
  if (isOpen) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-amber-400">
        <path 
          d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2v11z" 
          stroke="currentColor" 
          strokeWidth="2" 
          fill="currentColor"
          fillOpacity="0.2"
        />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-amber-400/70">
      <path 
        d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2v11z" 
        stroke="currentColor" 
        strokeWidth="2" 
      />
    </svg>
  );
}

function FileIcon({ filename }: { filename: string }) {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  
  // Color based on file type
  const colorClass = getFileIconColor(ext);
  
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={colorClass}>
      <path 
        d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      <path 
        d="M14 2v6h6" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      {/* File type indicator */}
      <text 
        x="12" 
        y="17" 
        fontSize="6" 
        fill="currentColor" 
        textAnchor="middle" 
        fontWeight="bold"
      >
        {getFileTypeLabel(ext)}
      </text>
    </svg>
  );
}

function getFileIconColor(ext: string): string {
  const colors: Record<string, string> = {
    js: 'text-yellow-400',
    mjs: 'text-yellow-400',
    jsx: 'text-yellow-400',
    ts: 'text-blue-400',
    tsx: 'text-blue-400',
    json: 'text-yellow-600',
    html: 'text-orange-400',
    htm: 'text-orange-400',
    css: 'text-blue-500',
    scss: 'text-pink-400',
    md: 'text-white/60',
    txt: 'text-white/50',
  };
  return colors[ext] || 'text-white/50';
}

function getFileTypeLabel(ext: string): string {
  const labels: Record<string, string> = {
    js: 'JS',
    mjs: 'JS',
    jsx: 'JSX',
    ts: 'TS',
    tsx: 'TSX',
    json: '{ }',
    html: '<>',
    htm: '<>',
    css: '#',
    scss: '#',
    md: 'MD',
  };
  return labels[ext] || '';
}

